import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getToken } from '../../shared/auth';

// Admin API 헬퍼 함수
const adminFetch = async (endpoint, options = {}) => {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers
  };

  return fetch(`https://api.ilouli.com${endpoint}`, {
    ...options,
    headers,
    credentials: 'include'
  });
};

const ProjectManagement = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  // 뷰 상태: 'list' | 'detail' | 'create' | 'edit'
  const [viewMode, setViewMode] = useState('list');
  const [selectedPost, setSelectedPost] = useState(null);

  // 폼 상태
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    status: 'active',
    priority: 'normal'
  });

  // 필터 상태
  const [statusFilter, setStatusFilter] = useState('all');

  // 게시글 목록 로드
  const loadPosts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }

      const response = await adminFetch(`/api/admin/projects?${params}`);

      if (response.ok) {
        const data = await response.json();
        setPosts(data.posts || []);
      } else {
        setMessage('게시글 목록을 불러오는데 실패했습니다.');
      }
    } catch (err) {
      setMessage('서버 연결에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && user.tier === 'admin') {
      loadPosts();
    }
  }, [user, statusFilter]);

  // 상세보기
  const viewPost = (post) => {
    setSelectedPost(post);
    setViewMode('detail');
  };

  // 목록으로 돌아가기
  const goToList = () => {
    setViewMode('list');
    setSelectedPost(null);
    setFormData({
      title: '',
      content: '',
      status: 'active',
      priority: 'normal'
    });
  };

  // 새 게시글 작성 화면
  const goToCreate = () => {
    setFormData({
      title: '',
      content: '',
      status: 'active',
      priority: 'normal'
    });
    setViewMode('create');
  };

  // 수정 화면
  const goToEdit = (post) => {
    setSelectedPost(post);
    setFormData({
      title: post.title,
      content: post.content,
      status: post.status,
      priority: post.priority
    });
    setViewMode('edit');
  };

  // 게시글 작성
  const handleCreate = async () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      setMessage('제목과 내용을 모두 입력해주세요.');
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    try {
      const response = await adminFetch('/api/admin/projects', {
        method: 'POST',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setMessage('게시글이 작성되었습니다.');
        goToList();
        loadPosts();
      } else {
        const data = await response.json();
        setMessage(data.error || '게시글 작성에 실패했습니다.');
      }
    } catch (err) {
      setMessage('서버 연결에 실패했습니다.');
    }
    setTimeout(() => setMessage(''), 3000);
  };

  // 게시글 수정
  const handleUpdate = async () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      setMessage('제목과 내용을 모두 입력해주세요.');
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    try {
      const response = await adminFetch(`/api/admin/projects/${selectedPost.id}`, {
        method: 'PUT',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setMessage('게시글이 수정되었습니다.');
        goToList();
        loadPosts();
      } else {
        const data = await response.json();
        setMessage(data.error || '게시글 수정에 실패했습니다.');
      }
    } catch (err) {
      setMessage('서버 연결에 실패했습니다.');
    }
    setTimeout(() => setMessage(''), 3000);
  };

  // 게시글 삭제
  const handleDelete = async (postId) => {
    if (!window.confirm('이 게시글을 삭제하시겠습니까?')) return;

    try {
      const response = await adminFetch(`/api/admin/projects/${postId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setMessage('게시글이 삭제되었습니다.');
        goToList();
        loadPosts();
      } else {
        const data = await response.json();
        setMessage(data.error || '게시글 삭제에 실패했습니다.');
      }
    } catch (err) {
      setMessage('서버 연결에 실패했습니다.');
    }
    setTimeout(() => setMessage(''), 3000);
  };

  // 날짜 포맷
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // 상태 뱃지 스타일
  const getStatusStyle = (status) => {
    switch (status) {
      case 'active': return 'bg-blue-50 text-blue-700';
      case 'completed': return 'bg-green-50 text-green-700';
      case 'archived': return 'bg-gray-100 text-gray-600';
      default: return '';
    }
  };

  // 상태 라벨
  const getStatusLabel = (status) => {
    switch (status) {
      case 'active': return '진행중';
      case 'completed': return '완료';
      case 'archived': return '보관';
      default: return status;
    }
  };

  // 우선순위 뱃지 스타일
  const getPriorityStyle = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-50 text-red-700';
      case 'normal': return 'bg-amber-50 text-amber-700';
      case 'low': return 'bg-gray-100 text-gray-600';
      default: return '';
    }
  };

  // 우선순위 라벨
  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 'high': return '높음';
      case 'normal': return '보통';
      case 'low': return '낮음';
      default: return priority;
    }
  };

  if (loading && viewMode === 'list') {
    return <div className="text-center text-gray-500 py-16 text-base">게시글을 불러오는 중...</div>;
  }

  // 상세보기 화면
  if (viewMode === 'detail' && selectedPost) {
    return (
      <div className="w-full">
        {message && (
          <div className={`px-5 py-4 rounded-lg mb-6 text-sm font-medium flex items-center gap-2.5 ${
            message.includes('실패') ? 'bg-red-50 text-red-600 border border-red-200' : 'bg-green-50 text-green-600 border border-green-200'
          }`}>
            {message}
          </div>
        )}

        <div className="w-full">
          <div className="flex justify-between items-center mb-8 pb-4 border-b border-gray-200">
            <button
              className="inline-flex items-center gap-1 px-4 py-2.5 bg-gray-100 border-none rounded-lg text-sm font-medium text-gray-600 cursor-pointer transition-all duration-200 hover:bg-gray-200 hover:text-gray-900"
              onClick={goToList}
            >
              ← 목록
            </button>
            <div className="flex gap-2">
              <button
                className="px-3 py-1.5 border-none rounded-md text-xs font-semibold cursor-pointer transition-all duration-150 bg-blue-50 text-blue-600 hover:bg-blue-100"
                onClick={() => goToEdit(selectedPost)}
              >
                수정
              </button>
              <button
                className="px-3 py-1.5 border-none rounded-md text-xs font-semibold cursor-pointer transition-all duration-150 bg-red-50 text-red-600 hover:bg-red-200"
                onClick={() => handleDelete(selectedPost.id)}
              >
                삭제
              </button>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-8">
            <div className="flex gap-2 mb-5">
              <span className={`inline-block px-2.5 py-1 rounded text-xs font-bold tracking-wide ${getStatusStyle(selectedPost.status)}`}>
                {getStatusLabel(selectedPost.status)}
              </span>
              <span className={`inline-block px-2.5 py-1 rounded text-xs font-bold tracking-wide ${getPriorityStyle(selectedPost.priority)}`}>
                {getPriorityLabel(selectedPost.priority)}
              </span>
            </div>

            <h1 className="text-3xl font-bold text-gray-900 mb-5 leading-snug tracking-tight">{selectedPost.title}</h1>

            <div className="flex flex-wrap gap-5 mb-8 pb-5 border-b border-gray-200 text-sm text-gray-500">
              <span>작성자: {selectedPost.author_name || '알 수 없음'}</span>
              <span>작성일: {formatDate(selectedPost.created_at)}</span>
              {selectedPost.updated_at !== selectedPost.created_at && (
                <span>수정일: {formatDate(selectedPost.updated_at)}</span>
              )}
            </div>

            <div className="text-base text-gray-900 leading-relaxed">
              {(selectedPost.content || '').split('\n').map((line, idx) => (
                <p key={idx} className="mb-4">{line || '\u00A0'}</p>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 작성/수정 화면
  if (viewMode === 'create' || viewMode === 'edit') {
    return (
      <div className="w-full">
        {message && (
          <div className={`px-5 py-4 rounded-lg mb-6 text-sm font-medium flex items-center gap-2.5 ${
            message.includes('실패') ? 'bg-red-50 text-red-600 border border-red-200' : 'bg-green-50 text-green-600 border border-green-200'
          }`}>
            {message}
          </div>
        )}

        <div className="w-full">
          <div className="flex items-center gap-5 mb-8 pb-4 border-b border-gray-200">
            <button
              className="inline-flex items-center gap-1 px-4 py-2.5 bg-gray-100 border-none rounded-lg text-sm font-medium text-gray-600 cursor-pointer transition-all duration-200 hover:bg-gray-200 hover:text-gray-900"
              onClick={goToList}
            >
              ← 목록
            </button>
            <h2 className="text-xl font-bold text-gray-900 m-0">{viewMode === 'create' ? '새 게시글 작성' : '게시글 수정'}</h2>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-8">
            <div className="flex flex-col gap-5">
              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-gray-900">제목</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="게시글 제목을 입력하세요"
                  className="px-4 py-3 border border-gray-200 rounded-lg text-sm font-sans transition-all duration-200 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10"
                />
              </div>

              <div className="flex gap-4 max-md:flex-col">
                <div className="flex-1 flex flex-col gap-2">
                  <label className="text-sm font-semibold text-gray-900">상태</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="px-4 py-3 border border-gray-200 rounded-lg text-sm font-sans transition-all duration-200 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10"
                  >
                    <option value="active">진행중</option>
                    <option value="completed">완료</option>
                    <option value="archived">보관</option>
                  </select>
                </div>
                <div className="flex-1 flex flex-col gap-2">
                  <label className="text-sm font-semibold text-gray-900">우선순위</label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    className="px-4 py-3 border border-gray-200 rounded-lg text-sm font-sans transition-all duration-200 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10"
                  >
                    <option value="high">높음</option>
                    <option value="normal">보통</option>
                    <option value="low">낮음</option>
                  </select>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-gray-900">내용</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="게시글 내용을 입력하세요"
                  rows={15}
                  className="px-4 py-3 border border-gray-200 rounded-lg text-sm font-sans resize-y min-h-[200px] leading-relaxed transition-all duration-200 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10"
                />
              </div>

              <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
                <button
                  className="min-h-[44px] px-6 py-3 bg-gray-100 border-none rounded-lg text-sm font-semibold text-gray-600 cursor-pointer transition-all duration-200 hover:bg-gray-200"
                  onClick={goToList}
                >
                  취소
                </button>
                <button
                  className="min-h-[44px] px-8 py-3 bg-gray-900 border-none rounded-lg text-sm font-semibold text-white cursor-pointer transition-all duration-200 hover:bg-gray-700"
                  onClick={viewMode === 'create' ? handleCreate : handleUpdate}
                >
                  {viewMode === 'create' ? '작성' : '저장'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 목록 화면
  return (
    <div className="w-full">
      {message && (
        <div className={`px-5 py-4 rounded-lg mb-6 text-sm font-medium flex items-center gap-2.5 ${
          message.includes('실패') ? 'bg-red-50 text-red-600 border border-red-200' : 'bg-green-50 text-green-600 border border-green-200'
        }`}>
          {message}
        </div>
      )}

      {/* 헤더 */}
      <div className="flex justify-between items-center mb-6 pb-4 border-b-2 border-gray-200 max-md:flex-col max-md:gap-4 max-md:items-start">
        <div className="flex items-baseline gap-3">
          <h2 className="text-xl font-bold text-gray-900 m-0 tracking-tight">프로젝트 관리</h2>
          <span className="text-sm text-gray-500 font-medium">{posts.length}개의 게시글</span>
        </div>
        <button
          className="min-h-[44px] px-6 py-3 bg-gray-900 text-white border-none rounded-lg text-sm font-semibold cursor-pointer transition-all duration-200 whitespace-nowrap shadow-sm hover:bg-gray-700 hover:-translate-y-0.5 hover:shadow-md max-md:w-full"
          onClick={goToCreate}
        >
          + 새 게시글
        </button>
      </div>

      {/* 필터 */}
      <div className="flex flex-col gap-4 p-5 bg-white border border-gray-200 rounded-xl mb-6 shadow-sm">
        <div className="flex flex-col gap-3">
          <span className="text-xs font-bold text-gray-500 uppercase tracking-widest">상태:</span>
          <div className="flex gap-2 flex-wrap">
            {['all', 'active', 'completed', 'archived'].map(status => (
              <button
                key={status}
                className={`min-h-[36px] px-4 py-2 border rounded-full text-sm font-medium cursor-pointer transition-all duration-200 ${
                  statusFilter === status
                    ? 'bg-gray-900 text-white border-gray-900 font-semibold shadow-md'
                    : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-200 hover:text-gray-900 hover:border-gray-300'
                }`}
                onClick={() => setStatusFilter(status)}
              >
                {status === 'all' ? '전체' : getStatusLabel(status)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* 게시글 목록 */}
      {posts.length === 0 ? (
        <div className="text-center text-gray-500 py-16 bg-gray-50 border border-gray-200 rounded-xl text-base leading-relaxed">
          <p className="mb-5">등록된 게시글이 없습니다.</p>
          <button
            className="min-h-[44px] px-6 py-3 bg-blue-600 text-white border-none rounded-lg text-sm font-semibold cursor-pointer transition-all duration-200 hover:bg-blue-700"
            onClick={goToCreate}
          >
            첫 게시글 작성하기
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {posts.map((post) => (
            <div
              key={post.id}
              className="bg-white border border-gray-200 rounded-xl p-6 transition-all duration-200 shadow-sm cursor-pointer hover:shadow-md hover:border-gray-300"
              onClick={() => viewPost(post)}
            >
              <div className="flex justify-between items-center mb-3 max-md:flex-col max-md:gap-3 max-md:items-start">
                <div className="flex gap-2">
                  <span className={`inline-block px-2.5 py-1 rounded text-xs font-bold tracking-wide ${getStatusStyle(post.status)}`}>
                    {getStatusLabel(post.status)}
                  </span>
                  <span className={`inline-block px-2.5 py-1 rounded text-xs font-bold tracking-wide ${getPriorityStyle(post.priority)}`}>
                    {getPriorityLabel(post.priority)}
                  </span>
                </div>
                <div className="flex gap-2 max-md:w-full max-md:justify-start" onClick={(e) => e.stopPropagation()}>
                  <button
                    className="px-3 py-1.5 border-none rounded-md text-xs font-semibold cursor-pointer transition-all duration-150 bg-blue-50 text-blue-600 hover:bg-blue-100"
                    onClick={() => goToEdit(post)}
                    title="수정"
                  >
                    수정
                  </button>
                  <button
                    className="px-3 py-1.5 border-none rounded-md text-xs font-semibold cursor-pointer transition-all duration-150 bg-red-50 text-red-600 hover:bg-red-200"
                    onClick={() => handleDelete(post.id)}
                    title="삭제"
                  >
                    삭제
                  </button>
                </div>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 cursor-pointer leading-snug tracking-tight hover:text-blue-600">
                {post.title}
              </h3>
              <p className="text-sm text-gray-500 mb-4 leading-relaxed break-words">
                {post.content.length > 150 ? post.content.substring(0, 150) + '...' : post.content}
              </p>
              <div className="flex gap-4 text-sm text-gray-500 max-md:flex-col max-md:gap-1">
                <span>{post.author_name}</span>
                <span className="text-gray-400">{formatDate(post.created_at)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProjectManagement;
